import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Check, FileText, MessageSquare, Pencil, Send, Trash2, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminChatSession = () => {
  let id = localStorage.getItem("pm_admin_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("pm_admin_chat_session", id);
  }
  return id;
};

function AgentChat({ agent, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: `Bonjour, je suis ${agent.name}. ${agent.role}` },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setLoading(true);
    try {
      const resp = await fetch(`${API}/admin/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          agent_key: agent.key,
          session_id: getAdminChatSession(),
          message: text,
        }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = {
                  role: "assistant",
                  text: next[next.length - 1].text + parsed.delta,
                };
                return next;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", text: parsed.error };
                return next;
              });
            }
          } catch {
            /* chunk incomplet */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", text: "Une erreur est survenue. Réessayez." };
        return next;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-5 rounded-lg border border-gold/25 bg-ink/60" data-testid={`agent-chat-${agent.key}`}>
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-gold">
          <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
          Discuter avec {agent.name}
        </p>
        <button onClick={onClose} aria-label="Fermer le chat" data-testid={`agent-chat-close-${agent.key}`}>
          <X className="h-4 w-4 text-ivory/40 hover:text-ivory" strokeWidth={1.5} />
        </button>
      </div>
      <div className="h-64 space-y-3 overflow-y-auto px-4 py-3" data-testid={`agent-chat-messages-${agent.key}`}>
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] whitespace-pre-line rounded-lg px-3 py-2 text-xs leading-relaxed ${
                m.role === "user" ? "bg-copper text-ivory" : "border border-white/10 bg-white/5 text-ivory/85"
              }`}
            >
              {m.text || (loading && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-white/10 p-3">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Demandez à ${agent.name}…`}
          className="input-dark !h-9 flex-1 !text-xs"
          data-testid={`agent-chat-input-${agent.key}`}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Envoyer"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-copper text-ivory transition-colors hover:bg-copperdark disabled:opacity-40"
          data-testid={`agent-chat-send-${agent.key}`}
        >
          <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
}

function AgentKnowledge({ agent }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    api.get(`/admin/agents/${agent.key}/knowledge`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.key]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/agents/${agent.key}/knowledge`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document ajouté aux connaissances de l'agent.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (docId) => {
    await api.delete(`/admin/agents/${agent.key}/knowledge/${docId}`);
    toast.success("Document retiré.");
    load();
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`agent-knowledge-${agent.key}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory/40">
          Formation — {docs.length} document(s)
        </p>
        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-gold transition-colors hover:text-ivory" data-testid={`agent-knowledge-upload-${agent.key}`}>
          <Upload className="h-3.5 w-3.5" strokeWidth={1.5} />
          {uploading ? "Lecture…" : "Ajouter (PDF, DOCX, XLSX, TXT)"}
          <input type="file" accept=".pdf,.docx,.xlsx,.xls,.txt" onChange={upload} className="hidden" />
        </label>
      </div>
      {docs.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {docs.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2 text-xs text-ivory/60" data-testid={`agent-knowledge-doc-${doc.id}`}>
              <FileText className="h-3.5 w-3.5 shrink-0 text-gold/60" strokeWidth={1.5} />
              <span className="min-w-0 flex-1 truncate">{doc.filename}</span>
              <button onClick={() => remove(doc.id)} aria-label="Retirer" className="text-ivory/30 transition-colors hover:text-red-400" data-testid={`agent-knowledge-delete-${doc.id}`}>
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AgentsManager() {
  const [agents, setAgents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [chatAgent, setChatAgent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const toggle = async (agent, enabled) => {
    try {
      await api.put(`/admin/agents/${agent.key}`, { enabled });
      toast.success(
        enabled
          ? `${agent.name} activé — il répond maintenant aux visiteurs dans le chat.`
          : `${agent.name} désactivé — l'Agent Univers prend le relais.`
      );
      load();
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/agents/${editing.key}`, {
        role: editing.role,
        system_prompt: editing.system_prompt,
      });
      toast.success("Agent mis à jour.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const visitorAgents = agents.filter((a) => a.kind !== "admin");
  const adminAgents = agents.filter((a) => a.kind === "admin");

  const renderAgent = (agent) => (
    <div key={agent.key} className="rounded-lg border border-white/10 bg-coal/60 p-6" data-testid={`agent-card-${agent.key}`}>
      <div className="flex items-start justify-between gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gold/30 text-gold">
          <Bot className="h-5 w-5" strokeWidth={1.25} />
        </span>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${agent.enabled ? "text-gold" : "text-ivory/30"}`} data-testid={`agent-status-${agent.key}`}>
            {agent.enabled ? "Activé" : "Désactivé"}
          </span>
          <Switch checked={!!agent.enabled} onCheckedChange={(v) => toggle(agent, v)} data-testid={`agent-toggle-${agent.key}`} />
        </div>
      </div>
      <h3 className="mt-4 text-base font-semibold text-ivory">{agent.name}</h3>
      <p className="mt-2 text-xs leading-relaxed text-ivory/50">{agent.role}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {(agent.capabilities || []).map((c) => (
          <span key={c} className="flex items-center gap-1 rounded border border-white/10 px-2 py-0.5 text-[10px] text-ivory/50">
            <Check className="h-3 w-3 text-gold" strokeWidth={1.5} />
            {c}
          </span>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <button
          onClick={() => setEditing({ ...agent })}
          className="btn-outline !h-9 !px-4 !text-[11px]"
          data-testid={`agent-edit-${agent.key}`}
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
          Instructions
        </button>
        {agent.kind === "admin" && (
          <button
            onClick={() => setChatAgent(chatAgent?.key === agent.key ? null : agent)}
            className="btn-primary !h-9 !px-4 !text-[11px]"
            data-testid={`agent-chat-open-${agent.key}`}
          >
            <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
            Discuter
          </button>
        )}
      </div>
      <AgentKnowledge agent={agent} />
      {chatAgent?.key === agent.key && (
        <AgentChat agent={agent} onClose={() => setChatAgent(null)} />
      )}
    </div>
  );

  return (
    <div data-testid="agents-manager">
      <h2 className="mb-2 text-lg font-semibold text-ivory">Centre des agents IA</h2>
      <p className="mb-8 max-w-2xl text-xs leading-relaxed text-ivory/40">
        Les agents « Visiteurs » répondent dans le chat du site avec routage automatique. Les agents
        « Équipe » vous assistent ici même avec les données de votre activité. Formez chaque agent
        avec vos documents (PDF, Word, Excel, texte) — leurs connaissances se mettent aussi à jour
        automatiquement avec vos contenus (carte, produits, prestations).
      </p>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="agent-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">{editing.name}</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Rôle de l'agent</label>
              <textarea rows={3} value={editing.role} onChange={(e) => setEditing((s) => ({ ...s, role: e.target.value }))} className="input-dark !h-auto py-3" data-testid="agent-role-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                Instructions (prompt système)
              </label>
              <textarea rows={6} value={editing.system_prompt} onChange={(e) => setEditing((s) => ({ ...s, system_prompt: e.target.value }))} className="input-dark !h-auto py-3" data-testid="agent-prompt-input" />
              <p className="mt-2 text-xs text-ivory/35">
                Ces instructions guideront le comportement de l'agent lorsqu'il sera activé.
              </p>
            </div>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="agent-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      <p className="overline mb-4">Agents visiteurs — chat du site</p>
      <div className="grid gap-4 md:grid-cols-2" data-testid="agents-visitor-list">
        {visitorAgents.map(renderAgent)}
      </div>

      <p className="overline mb-4 mt-12">Agents équipe — assistance back-office</p>
      <div className="grid gap-4 md:grid-cols-2" data-testid="agents-admin-list">
        {adminAgents.map(renderAgent)}
      </div>
    </div>
  );
}
